import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useAuthStore from "../../../store/authStore";
import { downloadDocumentBlob } from "../../assets/services/assetDocumentsService";
import { getDocumentById } from "../../assets/services/assetHoldingsService";

const DocumentPreviewPage = () => {
  const { type, documentId } = useParams();
  const [searchParams] = useSearchParams();

  const authUserId = useAuthStore((s) => s.userId ?? s.user?.userId ?? s.user);
  const authRoleId = useAuthStore((s) => s.roleId ?? s.user?.roleId ?? s.userRole);

  // When opened in a new tab, auth state is often missing because we persist to localStorage.
  // Accept userId/appRoleId via query params from the opener.
  const queryUserId = useMemo(() => Number(searchParams.get("userId")) || 0, [searchParams]);
  const queryAppRoleId = useMemo(() => Number(searchParams.get("appRoleId")) || 0, [searchParams]);

  const openerAuth = useMemo(() => {
    try {
      const opener = window.opener;
      if (!opener) return null;
      // same-origin only; will throw if not allowed
      const raw = opener.localStorage?.getItem("auth-storage");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const state = parsed?.state ?? parsed;
      return {
        token: state?.token || "",
        user: Number(state?.user) || 0,
        role: Number(state?.role) || 0,
      };
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    // Hydrate this tab's store from the opener tab, so API calls have auth if needed.
    try {
      if (!openerAuth) return;
      const current = useAuthStore.getState();
      if (!current.token && openerAuth.token) {
        useAuthStore.setState({ token: openerAuth.token }, false);
      }
      if (!current.user && openerAuth.user) {
        useAuthStore.setState({ user: openerAuth.user }, false);
      }
      if (!current.role && openerAuth.role) {
        useAuthStore.setState({ role: openerAuth.role }, false);
      }
    } catch {
      // ignore
    }
  }, [openerAuth]);

  useEffect(() => {
    const handler = (event) => {
      try {
        if (event.origin !== window.location.origin) return;
        const data = event.data || {};
        if (data.type !== "DOC_PREVIEW_AUTH") return;

        if (data.token) {
          useAuthStore.setState({ token: String(data.token) }, false);
        }
        if (data.userId) {
          useAuthStore.setState({ user: Number(data.userId) || 0 }, false);
        }
        if (data.appRoleId) {
          useAuthStore.setState({ role: Number(data.appRoleId) || 0 }, false);
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const effectiveUserId = queryUserId || (Number(authUserId) || 0) || (openerAuth?.user || 0);
  const effectiveAppRoleId = queryAppRoleId || (Number(authRoleId) || 0) || (openerAuth?.role || 0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fallbackUrl, setFallbackUrl] = useState("");

  const normalizedType = String(type || "").toLowerCase();
  const isPdf = normalizedType === "pdf" || normalizedType === ".pdf" || normalizedType.endsWith("pdf");
  const docId = Number(documentId) || 0;

  useEffect(() => {
    document.title = `Document Preview ${docId || ""}`.trim();
  }, [docId]);

  useEffect(() => {
    if (!fallbackUrl) return undefined;
    return () => {
      try {
        window.URL.revokeObjectURL(fallbackUrl);
      } catch {
        // ignore
      }
    };
  }, [fallbackUrl]);

  useEffect(() => {
    let isCancelled = false;

    const fetchAndLoadPdf = async () => {
      if (!isPdf || !docId) return;

      setLoading(true);
      setError("");
      setFallbackUrl("");

      try {
        const res = await getDocumentById(docId);
        const base64 = res?.data?.documentContent;

        if (!base64) throw new Error("Empty document response.");

        // Convert Base64 -> Binary
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        // Create Blob
        const blob = new Blob([byteArray], { type: "application/pdf" });

        // Create URL
        const blobUrl = window.URL.createObjectURL(blob);

        setFallbackUrl(blobUrl);
      } catch (e) {
        if (isCancelled) return;
        setError(e?.message || "Failed to load document.");
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchAndLoadPdf();
    return () => {
      isCancelled = true;
    };
  }, [docId, effectiveAppRoleId, effectiveUserId, isPdf]);

  if (!isPdf) {
    return (
      <div style={{ padding: 16, fontFamily: "Arial, sans-serif" }}>
        <h3>Preview not supported</h3>
        <p>Only PDF preview is supported here.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 0, margin: 0 }}>
      {loading && <div style={{ padding: 12, fontFamily: "Arial, sans-serif" }}>Loading…</div>}
      {!loading && error && <div style={{ padding: 12, color: "#b00020", fontFamily: "Arial, sans-serif" }}>{error}</div>}

      {!loading && fallbackUrl && (
        <iframe
          title="Document"
          src={fallbackUrl}
          style={{ width: "100%", height: "100vh", border: 0, display: "block" }}
        />
      )}

      {!loading && !fallbackUrl && !error && (
        <div style={{ padding: 12, fontFamily: "Arial, sans-serif" }}>No preview available.</div>
      )}
    </div>
  );
};

export default DocumentPreviewPage;
